import { XIcon } from "lucide-react";
import { useMemo, useRef, useState } from "react";

import { Map, type MapHandle, type MapPin } from "@/web/components/styled/Map/Map";
import { ExploreLegend } from "@/web/routes/_protected/smb/-dashboard/-actions/-explore/ExploreLegend";
import { ExplorePropertyCard } from "@/web/routes/_protected/smb/-dashboard/-actions/-explore/ExplorePropertyCard";
import { ExploreSearchBar } from "@/web/routes/_protected/smb/-dashboard/-actions/-explore/ExploreSearchBar";
import {
  explorePinColor,
  type ExplorePin,
} from "@/web/routes/_protected/smb/-dashboard/-actions/-explore/types";

// Off-market pins sit beneath active listings; a selected pin always comes to
// the front (handled by the shared Map via `selected`).
const Z_OFF_MARKET = 1;
const Z_DEFAULT = 2;

type ExploreMapProps = {
  pins: ExplorePin[];
  /** Selection keys currently staged. */
  selectedKeys: ReadonlySet<string>;
  /** Toggle a pin's membership in the staged selection. */
  onToggle: (pin: ExplorePin) => void;
  /** Add a searched (off-catalog) pin to the map + select it. */
  onSearchSelect: (pin: ExplorePin) => void;
  /** Whether the cap is reached (un-selected pins can't be added). */
  atCap: boolean;
  /** Header count of staged selections. */
  selectedCount: number;
  maxSelections: number;
  onClose: () => void;
  /** Pinned footer (e.g. the Confirm button) rendered below the bottom section. */
  footer?: React.ReactNode;
};

/**
 * Full-bleed Explore map: pins for every targetable property, an overlaid
 * header (staged count + close), an address search bar, a color-key legend, and
 * a bottom section that defaults to a horizontal scroller of every property and
 * collapses to a single card with an Add/Remove button when a pin is tapped.
 */
export function ExploreMap({
  pins,
  selectedKeys,
  onToggle,
  onSearchSelect,
  atCap,
  selectedCount,
  maxSelections,
  onClose,
  footer,
}: ExploreMapProps) {
  const mapRef = useRef<MapHandle>(null);
  // The tapped pin whose single card replaces the scroller. `null` → scroller.
  const [focusedKey, setFocusedKey] = useState<string | null>(null);
  const focused = focusedKey ? pins.find((p) => p.key === focusedKey) : undefined;

  const isSelected = (key: string) => selectedKeys.has(key);
  const isDisabled = (key: string) => atCap && !isSelected(key);

  const mapPins = useMemo<MapPin<ExplorePin>[]>(
    () =>
      pins.map((pin) => {
        const selected = isSelected(pin.key);
        return {
          id: pin.key,
          latitude: pin.latitude,
          longitude: pin.longitude,
          color: explorePinColor(pin.category, selected),
          selected,
          zIndex: selected ? undefined : pin.category === "offMarket" ? Z_OFF_MARKET : Z_DEFAULT,
          onClick: () => setFocusedKey(pin.key),
          data: pin,
        };
      }),
    [pins, selectedKeys, atCap],
  );

  const headerLabel =
    selectedCount === 1 ? "1 target property" : `${selectedCount} target properties`;

  const recenterOn = (pin: ExplorePin) => {
    const d = 0.01;
    mapRef.current?.focusBounds(
      [
        [pin.longitude - d, pin.latitude - d],
        [pin.longitude + d, pin.latitude + d],
      ],
      { padding: 80 },
    );
  };

  return (
    <div className="flex h-full w-full flex-col overflow-hidden">
      {/* Map: grows to fill the space left above the pinned footer, clamped at
          800px (and never shorter than 240px). Using `flex-1` rather than
          `h-full shrink-0` lets it shrink on short screens so the footer stays
          pinned at the bottom instead of being pushed off into a scroll.
          Header / search / legend / card section all overlay it; the card
          section is lifted off the bottom edge by `BOTTOM_INSET` so it doesn't
          sit flush against it. `data-vaul-no-drag` stops map pan/drag gestures
          from also dragging the surrounding vaul drawer (same fix CompMap
          uses). */}
      <div data-vaul-no-drag className="relative max-h-200 min-h-60 flex-1 overflow-hidden">
        <Map
          ref={mapRef}
          pins={mapPins}
          navigationControl={false}
          // Tapping an empty part of the map blurs the focused pin, reverting the
          // bottom section from the single card back to the scrollable list.
          onBackgroundClick={() => setFocusedKey(null)}
          className="absolute inset-0"
        />

        {/* Top overlay: header row + search bar. */}
        <div className="pointer-events-none absolute inset-x-0 top-0 z-10 flex flex-col gap-4 p-5">
          <div className="pointer-events-auto flex items-center justify-between">
            <span className="font-heading text-base text-withus-red uppercase">{headerLabel}</span>
            <button
              type="button"
              onClick={onClose}
              aria-label="Close"
              className="text-withus-dark-blue transition-opacity hover:opacity-70"
            >
              <XIcon className="size-5" />
            </button>
          </div>
          <ExploreSearchBar
            className="pointer-events-auto"
            onSelect={(pin) => {
              onSearchSelect(pin);
              setFocusedKey(pin.key);
              recenterOn(pin);
            }}
          />
        </div>

        {/* Bottom overlay stack (legend + count, then the card section), pinned
            `BOTTOM_INSET` (20px) above the map's bottom edge so the cards float
            over the map without touching its edge. The legend/count sit just
            above the cards. */}
        <div className="pointer-events-none absolute inset-x-0 bottom-5 z-10 flex flex-col gap-3">
          <div className="flex items-end justify-between px-5">
            <ExploreLegend />
            <span className="bg-withus-pure-white/90 px-2 py-1 text-xs text-withus-dark-blue shadow-sm">
              {selectedCount} of {maxSelections} selected
            </span>
          </div>

          {focused ? (
            <div className="pointer-events-auto px-5">
              <ExplorePropertyCard
                pin={focused}
                selected={isSelected(focused.key)}
                disabled={isDisabled(focused.key)}
                onToggle={() => onToggle(focused)}
                variant="full"
              />
            </div>
          ) : (
            <div
              data-vaul-no-drag
              className="pointer-events-auto flex gap-4 overflow-x-auto px-5 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
            >
              {pins.map((pin) => (
                <ExplorePropertyCard
                  key={pin.key}
                  pin={pin}
                  selected={isSelected(pin.key)}
                  disabled={isDisabled(pin.key)}
                  onToggle={() => onToggle(pin)}
                  variant="compact"
                />
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Pinned footer (Confirm), below the map. */}
      {footer ? <div className="shrink-0 px-5 py-4">{footer}</div> : null}
    </div>
  );
}
